/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : PageHelpButton
 *   Feature      : 226-universal-page-affordances
 *   Description  : Thin resolver that turns a page's `helpId` into the shared
 *                  PageHelpPanel (which renders the `?` trigger + drawer). Keeps
 *                  the registry lookup in one place and fails loudly in dev when
 *                  a page references an unregistered helpId (so the gap is caught
 *                  before the conformance guard runs).
 *   Constitution : v1.23.0
 *   Author       : Peter Dobler
 *   Created      : 2026-07-07
 * ============================================================================
 */
import React from 'react';
import { PageHelpPanel } from './PageHelpPanel';
import { getPageHelp } from '../../data/help/pages';

export interface PageHelpButtonProps {
  /** Registry key (route slug) for this page's help content. */
  helpId: string;
}

export const PageHelpButton: React.FC<PageHelpButtonProps> = ({ helpId }) => {
  const content = getPageHelp(helpId);

  if (!content) {
    // Fail loudly in dev; render nothing in prod rather than crash a page.
    if (import.meta.env.DEV) {
      throw new Error(
        `[PageHelpButton] No page help registered for helpId="${helpId}". ` +
          `Add an entry to src/data/help/pages/index.ts (Spec 226).`
      );
    }
    return null;
  }

  return <PageHelpPanel content={content} />;
};

export default PageHelpButton;
